import { useEffect, useMemo, useState } from 'react';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useFetchMerchant } from 'core/hooks/useFetchMerchant';
import { ExperienceEntity, ExperienceEntityTree, ExperienceEntityType } from 'modules/experience-core';
import useMerchantVenues from 'modules/merchant/hooks/useMerchantVenues';

export const useMerchant = (identifier: string, isMerchantFetchEnabled = true) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isVenuesLoading, setIsVenuesLoading] = useState(true);

  const { merchant, merchantError, isLoadingMerchant } = useFetchMerchant(identifier, isMerchantFetchEnabled);

  const {
    data,
    error: merchantVenuesError,
    isLoading: isLoadingMerchantVenues,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMerchantVenues(isMerchantFetchEnabled, identifier);

  useEffect(() => {
    if (isLoadingMerchantVenues || isFetchingNextPage) {
      return;
    }

    if (hasNextPage) {
      fetchNextPage();
    } else {
      setVenues(data?.pages.flatMap(page => page.venues) || []);
      setIsVenuesLoading(false);
    }
  }, [data?.pages, fetchNextPage, hasNextPage, isFetchingNextPage, isLoadingMerchantVenues]);

  const isLoading = isVenuesLoading || isLoadingMerchant || isLoadingMerchantVenues;
  const experienceEntity: ExperienceEntity | null = useMemo(() => {
    if (!merchant || isLoading) {
      return null;
    }
    return {
      experienceEntityObject: merchant,
      experienceEntityType: ExperienceEntityType.MERCHANT,
      experienceEntityId: merchant.itemid.$oid,
    };
  }, [merchant, isLoading]);

  const experienceEntityTree: ExperienceEntityTree | null = useMemo(() => {
    if (isLoading || !experienceEntity || !venues) {
      return null;
    }
    return {
      experienceEntity,
      children: venues.map(venue => ({
        experienceEntity: {
          experienceEntityObject: venue,
          experienceEntityType: ExperienceEntityType.VENUE,
          experienceEntityId: venue.id,
        },
        children: [],
      })),
    };
  }, [isLoading, experienceEntity, venues]);

  return {
    experienceEntity,
    experienceEntityTree,
    error: merchantError || merchantVenuesError,
    isLoading,
  };
};
